import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useData } from "@/context/DataContext";
import { useStats } from "@/hooks/useStats";
import { ds, money, periodLabel } from "@/lib/money";
import { t } from "@/lib/i18n";

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-[var(--bd)] bg-[var(--card)] px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold">{p.label}</div>
      <div className="num font-bold" style={{ color: p.value >= 0 ? "var(--ok)" : "var(--dg)" }}>{money(p.value)}</div>
    </div>
  );
}

export default function SavingsChart() {
  const { db, mode } = useData();
  const st = useStats();
  const data = useMemo(() => {
    const yr = mode === "year";
    const n = yr ? 12 : Math.round((st.e - st.s) / 864e5);
    const v = Array.from({ length: n }, (_, k) => {
      const d = yr ? new Date(st.s.getFullYear(), k, 1) : new Date(st.s.getFullYear(), st.s.getMonth(), st.s.getDate() + k);
      return { value: 0, label: yr ? ds(d, { month: "long" }) : ds(d, { weekday: "short", day: "numeric", month: "short" }), x: yr ? ds(d, { month: "short" }) : String(d.getDate()) };
    });
    db.savings.forEach((i) => {
      if (i.date < st.a || i.date >= st.z) return;
      const d = new Date(i.date + "T00:00");
      const k = yr ? d.getMonth() : Math.round((d - st.s) / 864e5);
      if (v[k]) v[k].value += i.amt;
    });
    return v;
  }, [db.savings, st, mode]);
  return (
    <div className="mt-4 rounded-[var(--ri)] border border-[var(--bd)] p-3" data-testid="savings-bar-graph">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-semibold">{t("Savings in this period")}</span>
        <span className="num text-xs text-[var(--mu)]">{periodLabel(mode, st.s, st.e)} · <b style={{ color: st.svP >= 0 ? "var(--ok)" : "var(--dg)" }}>{money(st.svP)}</b></span>
      </div>
      <div className="h-44">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--bd)" strokeDasharray="3 4" />
            <XAxis dataKey="x" tick={{ fill: "var(--mu)", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={10} />
            <YAxis tick={{ fill: "var(--mu)", fontSize: 10 }} axisLine={false} tickLine={false} width={48} />
            <ReferenceLine y={0} stroke="var(--bd)" />
            <Tooltip content={<Tip />} cursor={{ fill: "color-mix(in srgb, var(--ac) 10%, transparent)" }} />
            <Bar dataKey="value" radius={[6, 6, 2, 2]}>{data.map((d, i) => <Cell key={i} fill={d.value >= 0 ? "var(--ok)" : "var(--dg)"} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
